/**
 * Hook para gerenciamento de anexos de atividades CRM
 * Upload, listagem e deleção de arquivos vinculados a atividades
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.2.0 - Upload/delete passam a invalidar também
 *            ['deal-activity-attachments'] para refletir imediatamente na
 *            nova aba "Anexos" do modal de negócio.
 *   RELEASE: 2026-07-07 v1.1.0 - Upload/delete passam a invalidar também
 *            ['crm-deal-activities-paginated'] e ['crm-activities-feed'],
 *            garantindo que a timeline do modal de negócio reflita anexos
 *            imediatamente (sem exigir F5).
 *   RELEASE: 2026-01-04 v1.0.0 - Versão inicial.
 */


import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { sanitizeStorageFileName } from '@/lib/storageFileName';

// Logger para o módulo
const logger = createLogger('ActivityAttachments');

// Tipos de arquivo suportados
export type AttachmentFileType = 'image' | 'pdf' | 'document' | 'spreadsheet' | 'other';

// Interface do anexo
export interface ActivityAttachment {
  id: string;
  tenant_id: string;
  activity_id: string;
  file_name: string;
  file_type: AttachmentFileType;
  mime_type: string | null;
  file_size: number | null;
  storage_path: string;
  public_url: string;
  thumbnail_url: string | null;
  uploaded_by: string | null;
  created_at: string;
}

// Constantes de validação
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ATTACHMENTS = 10;

// Mapeamento de MIME types para tipos de arquivo
const ALLOWED_MIME_TYPES: Record<string, AttachmentFileType> = {
  'image/jpeg': 'image',
  'image/png': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'application/pdf': 'pdf',
  'application/msword': 'document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
  'text/plain': 'document',
  'application/vnd.ms-excel': 'spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'spreadsheet',
  'text/csv': 'spreadsheet',
};

/**
 * Determina o tipo de arquivo baseado no MIME type
 */
function getFileType(mimeType: string): AttachmentFileType {
  return ALLOWED_MIME_TYPES[mimeType] || 'other';
}

/**
 * Valida arquivo antes do upload
 */
function validateFile(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `Arquivo muito grande. Máximo: ${MAX_FILE_SIZE / 1024 / 1024}MB` };
  }
  
  if (!ALLOWED_MIME_TYPES[file.type]) {
    return { valid: false, error: 'Tipo de arquivo não suportado' };
  }
  
  return { valid: true };
}

/**
 * Sanitiza nome do arquivo para evitar problemas de segurança
 */
// v1.x: delegado ao utilitário central (remove emojis/acentos e evita
// o erro "Invalid key" do Supabase Storage). O nome original permanece no banco.
function sanitizeFileName(fileName: string): string {
  return sanitizeStorageFileName(fileName);
}

/**
 * Hook para gerenciar anexos de uma atividade
 */
export function useActivityAttachments(activityId: string | null) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  // Query para listar anexos da atividade
  const attachmentsQuery = useQuery({
    queryKey: ['activity-attachments', activityId],
    queryFn: async () => {
      if (!activityId || !currentTenant?.id) return [];

      const { data, error } = await supabase
        .from('crm_activity_attachments')
        .select('*')
        .eq('activity_id', activityId)
        .eq('tenant_id', currentTenant.id)
        .order('created_at', { ascending: true });

      if (error) {
        logger.error('Erro ao buscar anexos:', error.message);
        throw error;
      }
      
      return data as ActivityAttachment[];
    },
    enabled: !!activityId && !!currentTenant?.id,
    staleTime: 60 * 1000, // Cache de 1 minuto
  });

  // Mutation para upload de arquivo
  const uploadMutation = useMutation({
    mutationFn: async ({ 
      activityId, 
      file 
    }: { 
      activityId: string; 
      file: File;
    }) => {
      if (!currentTenant?.id || !user?.id) {
        throw new Error('Usuário não autenticado');
      }

      // Valida arquivo
      const validation = validateFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Gera path único no storage
      const timestamp = Date.now();
      const sanitizedName = sanitizeFileName(file.name);
      const storagePath = `activities/${activityId}/${timestamp}_${sanitizedName}`;

      // Upload para o bucket "anexos"
      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        logger.error('Erro no upload:', uploadError.message);
        throw new Error('Falha ao enviar arquivo');
      }

      // Obtém URL pública
      const { data: urlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      const fileType = getFileType(file.type);
      const thumbnailUrl = fileType === 'image' ? urlData.publicUrl : null;

      // Cria registro no banco
      const { data, error: insertError } = await supabase
        .from('crm_activity_attachments')
        .insert({
          tenant_id: currentTenant.id,
          activity_id: activityId,
          file_name: file.name,
          file_type: fileType,
          mime_type: file.type,
          file_size: file.size,
          storage_path: storagePath,
          public_url: urlData.publicUrl,
          thumbnail_url: thumbnailUrl,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (insertError) {
        // Rollback: remove arquivo do storage
        await supabase.storage.from('anexos').remove([storagePath]);
        logger.error('Erro ao registrar anexo:', insertError.message);
        throw new Error('Falha ao registrar anexo');
      }

      return data as ActivityAttachment;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['activity-attachments', variables.activityId] });
      // Timeline legada (hook antigo)
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities'] });
      // Timeline paginada do modal de negócio (fonte real hoje)
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated'] });
      // Feed global de atividades
      queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
      // v1.2.0: aba "Anexos" do modal de negócio
      queryClient.invalidateQueries({ queryKey: ['deal-activity-attachments'] });
      // v1.3.0: timelines do modal de cliente/contato (anexos vêm embutidos na RPC)
      queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['customer-activities'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },

  });

  // Mutation para deletar arquivo
  const deleteMutation = useMutation({
    mutationFn: async (attachment: ActivityAttachment) => {
      // Remove do storage
      const { error: storageError } = await supabase.storage
        .from('anexos')
        .remove([attachment.storage_path]);

      if (storageError) {
        logger.warn('Erro ao remover do storage:', storageError.message);
        // Continua para remover do banco mesmo se falhar no storage
      }

      // Remove do banco
      const { error: deleteError } = await supabase
        .from('crm_activity_attachments')
        .delete()
        .eq('id', attachment.id);

      if (deleteError) {
        throw deleteError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-attachments', activityId] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
      // v1.2.0: aba "Anexos" do modal de negócio
      queryClient.invalidateQueries({ queryKey: ['deal-activity-attachments'] });
      // v1.3.0: timelines do modal de cliente/contato
      queryClient.invalidateQueries({ queryKey: ['customer-activities-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['customer-activities'] });
      toast.success('Anexo removido');
    },


    onError: (error: Error) => {
      toast.error(`Erro ao remover anexo: ${error.message}`);
    },
  });

  return {
    attachments: attachmentsQuery.data || [],
    isLoading: attachmentsQuery.isLoading,
    error: attachmentsQuery.error,
    uploadFile: uploadMutation.mutateAsync,
    deleteAttachment: deleteMutation.mutate,
    isUploading: uploadMutation.isPending,
    isDeleting: deleteMutation.isPending,
    canAddMore: (attachmentsQuery.data?.length || 0) < MAX_ATTACHMENTS,
    maxAttachments: MAX_ATTACHMENTS,
  };
}

/**
 * Formata tamanho do arquivo para exibição
 */
export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}
